import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CatalogFacade } from '../../facades/catalog.facade';
import { take, takeUntil } from 'rxjs/operators';
import { ISdmIbComponents, ISdmIbComponentsData } from '../../models/catalog.models';
import { CartFacade } from '../entities/cart/facade/cart.facade';
import { ICartCreateWithEquipmentResponse } from '../../models/cart.models';
import { IconType } from '../../models/settings.model';
import { EFeatureRoutes } from '../configurations/app.configurations';
import { CustomerFacade } from '../../facades/customer.facade';
import { ISelectSoldTo } from '../../models/soldTo-selection.models';
import { IAddress } from '../../models/common.models';
import { SoldToAccountsFacade } from '../../facades/sold-to.facade';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { ShopCartActions } from '../../actions';

@Component({
  selector: 'app-external-redirect-loader',
  templateUrl: './external-redirect-loader.component.html',
  styleUrl: './external-redirect-loader.component.scss',
  standalone: false,
})
export class ExternalRedirectLoaderComponent implements OnInit, OnDestroy {
  protected readonly iconType: typeof IconType = IconType;
  protected readonly EFeatureRoutes: typeof EFeatureRoutes = EFeatureRoutes;

  // Route params
  sysivk!: string;
  flNumber!: string;
  material!: string;

  // SoldTo selection modal (for BP users)
  isSoldToSelectionModalVisible: boolean = false;
  soldToAddresses!: IAddress[];
  areSoldToAddressesLoaded: boolean = false;
  selectedSoldTo: IAddress | undefined = undefined;

  // Error handling (e.g. equipment cannot be selected for user)
  isErrorModalVisible: boolean = false;
  isBusinessPartner: boolean = false;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly cartFacade: CartFacade = inject(CartFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly soldToAccountsFacade: SoldToAccountsFacade = inject(SoldToAccountsFacade);
  private readonly store: Store = inject(Store);

  /**
   * On component initialization, required parameters are extracted from URL. Then, obtain if soldTo addresses are loaded for modal as we need to
   * block selecting of soldTo during loading. Then check if user is BP. If user is not BP, we can directly create cart with equipment and redirect
   * user to catalog or PDP based on presence of material parameter in URL.
   * If user is BP, soldTo addresses need to be inserted to modal window select box and selected before cart creation, so soldTo selection modal is opened.
   * After user selects soldTo address, create cart with equipment and selected soldTo address and redirect to catalog or PDP.
   * If there is an error during cart creation and equipment selection, error modal is shown to user.
   */
  ngOnInit(): void {
    // Params from external URL:
    this.sysivk = this.route.snapshot.queryParamMap.get('sysivk') || '';
    this.flNumber = this.route.snapshot.queryParamMap.get('fl-number') || '';
    this.material = this.route.snapshot.queryParamMap.get('material') || '';

    if (!this.sysivk || !this.flNumber) {
      throw new Error('Missing required parameters: sysivk and flNumber are required.');
    }

    this.soldToAccountsFacade.selectAreSoldToAddressesLoaded().pipe(takeUntil(this.unsubscribe$)).subscribe((areLoaded: boolean) => {
      this.areSoldToAddressesLoaded = areLoaded;
    });

    this.customerFacade.isBusinessPartner().pipe(take(1)).subscribe((isBp: boolean) => {
      if (isBp) {
        this.isBusinessPartner = isBp;
        this.soldToAccountsFacade.selectSoldToAccounts().pipe(takeUntil(this.unsubscribe$)).subscribe((soldToAddresses: IAddress[] | null) => {
          this.soldToAddresses = soldToAddresses ?? [];
          this.isSoldToSelectionModalVisible = true;
        });
      } else {
        this._createCartAndNavigate();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Creates new cart with equipment based on fl-number. After successful cart creation, user is redirected to catalog or product detail page,
   * depending on presence of material parameter in URL.
   * @private
   */
  private _createCartAndNavigate(): void {
    this.cartFacade.createNewCartWithEquipment(this.flNumber, this.selectedSoldTo).pipe(take(1)).subscribe({
      next: (carts: ICartCreateWithEquipmentResponse) => {
        this.cartFacade.setAllUserCarts(carts);
        this.cartFacade.setCurrentCart(carts.data[carts.data.length - 1]);
        if (this.material) {
          this._processNavigationToProductDetail(this.material);
        } else {
          this._processNavigationToCatalog(this.flNumber, this.sysivk);
        }
      },
      error: () => {
        this.isErrorModalVisible = true;
      },
    });
  }

  /**
   * Redirects user to product detail page. PDP requires sku value of product for redirection. Sku can be always created as "A_{material}".
   * @param {string} material
   * @private
   */
  private _processNavigationToProductDetail(material: string): void {
    const redirectUrl: string = `product/A_${material}`;
    this.router.navigate([redirectUrl]).then();
  }

  /**
   * Redirects user to catalog with preselected filters. Required parameters for redirection are fl-number and sysivk (as rel-product-sysivk).
   * Parameter rel-sparepart-ivk is built from material values from SdmIbComponents related to flNumber.
   * @param {string} flNumber
   * @param {string} sysivk
   * @private
   */
  private _processNavigationToCatalog(flNumber: string, sysivk: string): void {
    const redirectUrl: string = '/catalog/parts';
    const params: Params = {
      'fl-number': flNumber,
      'rel-product-sysivk': sysivk,
    };
    this.catalogFacade.getSdmIbComponents(flNumber).pipe(take(1)).subscribe((components: ISdmIbComponents) => {
      params['rel-sparepart-ivk'] = this._extractMaterialList(components.data);
      this.router.navigate([redirectUrl], {queryParams: params}).then();
    });
  }

  /**
   * Material values are used in catalog as rel-sparepart-ivk param, so we need to extract them from components response
   * and create comma separated string. In each sdm ib component, value is located in "attributes.material" field.
   * Example result in URL: "rel-sparepart-ivk=11111111,22222222,33333333".
   * @param {ISdmIbComponentsData[]} components
   * @returns {string}
   * @private
   */
  private _extractMaterialList(components: ISdmIbComponentsData[]): string {
    return components
      .map((component: ISdmIbComponentsData) => component.attributes.material)
      .filter(material => !!material)
      .join(',');
  }

  /**
   * Redirects user to the specified feature route. Used in error modal, when equipment cannot be selected for user.
   * @param {EFeatureRoutes} feature
   * @param {boolean} loadCarts
   */
  redirectTo(feature: EFeatureRoutes, loadCarts?: boolean): void {
    if (loadCarts) {
      this.store.dispatch(ShopCartActions.loadCarts());
      this.store.dispatch(ShopCartActions.loadCurrentCartAndCartItems());
    }
    this.router.navigate([feature]).then();
  }

  /**
   * Create cart after selecting sold to address by user in select sold to account modal.
   * @param {ISelectSoldTo} address
   */
  selectSoldToAddress(address: ISelectSoldTo): void {
    this.selectedSoldTo = this.soldToAddresses.find(soldToAddress => soldToAddress.id === address.id && soldToAddress.sapId === address.value);
    this._createCartAndNavigate();
  }
}
